import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Mail, Lock, User, ArrowRight, Check, Shield, Phone, Cpu, TrendingUp, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { fadeIn, fadeInLeft, fadeInScale, fadeInUp } from "../lib/animations";
import { isAdminEmail } from "../utils/admin";
import { track, identify, EVENTS, setStoredFunnelEmail } from "../lib/analytics";
import Mascot from "../components/Mascot";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Plan metadata for display ONLY — single source of truth for IDs is the backend.
// The IDs here MUST match backend/core/config.py PRICING_PLANS keys exactly.
const PLAN_DISPLAY = {
  solo_agent:    { name: "Solo Agent",       price: 149, hasTrial: false, accent: "from-blue-500 to-cyan-500" },
  market_leader: { name: "Market Leader",    price: 399, hasTrial: true,  accent: "from-purple-500 to-pink-500" },
  brokerage_moat:{ name: "Brokerage Moat",   price: 999, hasTrial: false, accent: "from-emerald-500 to-teal-500" },
};

const VALID_PLAN_IDS = new Set(Object.keys(PLAN_DISPLAY));

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(searchParams.get('forgot') === '1');
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  // Capture & resolve ?ref= referral code on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      sessionStorage.setItem('adwup_ref_code', refCode);
      axios.post(`${API_URL}/referrals/resolve`, { code: refCode })
        .then((res) => setReferrerInfo(res.data))
        .catch(() => {});
    } else {
      const stored = sessionStorage.getItem('adwup_ref_code');
      if (stored) {
        axios.post(`${API_URL}/referrals/resolve`, { code: stored })
          .then((res) => setReferrerInfo(res.data))
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Plan resolution — REACTIVE (recomputes whenever URL params change).
  // Strict precedence: URL > sessionStorage > default. Invalid values fall through to default.
  // sessionStorage is only used as a fallback if the URL param is missing AND it contains a
  // valid plan ID. This prevents stale data from a previous session from overriding a fresh URL.
  const selectedPlan = useMemo(() => {
    const fromUrl = searchParams.get('plan');
    if (fromUrl && VALID_PLAN_IDS.has(fromUrl)) return fromUrl;
    const fromStorage = sessionStorage.getItem('selectedPlan');
    if (fromStorage && VALID_PLAN_IDS.has(fromStorage)) return fromStorage;
    return 'market_leader';
  }, [searchParams]);

  // Whenever the resolved plan changes, persist it so checkout has a stable reference
  useEffect(() => {
    if (VALID_PLAN_IDS.has(selectedPlan)) {
      sessionStorage.setItem('selectedPlan', selectedPlan);
    }
  }, [selectedPlan]);

  const planDisplay = PLAN_DISPLAY[selectedPlan] || PLAN_DISPLAY.market_leader;
  const listingUrl = searchParams.get('url') || '';

  // Store listing URL so it survives Stripe redirect
  useEffect(() => {
    if (listingUrl) {
      localStorage.setItem('pendingListingUrl', listingUrl);
    }
  }, [listingUrl]);

  const { login, register, user, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user is already logged in and has active subscription, go to dashboard
    // (Admin users get auto-routed to /admin)
    if (user) {
      const adminUser = isAdminEmail(user.email);
      if (adminUser) {
        navigate('/admin', { replace: true });
        return;
      }
      if (hasActiveSubscription()) {
        navigate('/dashboard', { replace: true });
      }
    }
    // If user is logged in but no subscription, don't auto-redirect (let them register/checkout)
  }, [user, navigate, hasActiveSubscription]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Track signup intent — fire BEFORE the API call so we capture even on failures
    if (formData.email) setStoredFunnelEmail(formData.email);
    if (!isLogin) {
      track(EVENTS.REGISTRATION_STARTED, { plan: selectedPlan }, { email: formData.email });
    }

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        identify(userData);
        track(EVENTS.LOGIN_COMPLETED, { plan: userData?.plan }, { email: userData?.email });
        // Check if user has active subscription
        const sub = userData?.subscription;
        const hasActiveSub = sub && (sub.status === 'active' || sub.status === 'trialing');
        const adminUser = isAdminEmail(userData?.email || '');

        // Admin users always go straight to the admin panel
        if (adminUser) {
          toast.success("ACCESS GRANTED", { description: "Welcome back, Admin." });
          navigate('/admin', { replace: true });
          return;
        }

        if (hasActiveSub) {
          toast.success("ACCESS GRANTED");
          const pendingUrl = localStorage.getItem('pendingListingUrl');
          if (pendingUrl) {
            localStorage.removeItem('pendingListingUrl');
            navigate(`/dashboard?url=${encodeURIComponent(pendingUrl)}`, { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          // Regular user without an active subscription — send them to /pricing so
          // they consciously pick a plan (Audit iter58: never auto-start a Stripe
          // checkout for a plan the user didn't just choose).
          toast.info("Choose a plan to access your dashboard");
          navigate('/pricing?required=1', { replace: true });
        }
      } else {
        if (!formData.name.trim()) {
          toast.error("Name required");
          setIsLoading(false);
          return;
        }
        // Pass the selected plan to register
        const userData = await register(formData.email, formData.password, formData.name, selectedPlan);
        identify(userData);
        track(EVENTS.REGISTRATION_COMPLETED, { plan: selectedPlan }, { email: userData?.email });
        toast.success(`Setting up ${planDisplay.name}...`, { description: "Redirecting to secure checkout." });

        // Use selectedPlan as the SINGLE source of truth — no fallback shenanigans
        const checkoutPlan = VALID_PLAN_IDS.has(selectedPlan) ? selectedPlan : 'market_leader';
        sessionStorage.removeItem('selectedPlan');

        track(EVENTS.PLAN_SELECTED, { plan: checkoutPlan }, { email: userData?.email });
        try {
          const originUrl = window.location.origin;
          const response = await axios.post(`${API_URL}/billing/checkout`, {
            plan_id: checkoutPlan,
            origin_url: originUrl
          });

          if (response.data.url) {
            track(EVENTS.CHECKOUT_STARTED, { plan: checkoutPlan, has_trial: !!response.data.has_trial }, { email: userData?.email });
            if (response.data.has_trial) {
              toast.success("Starting your 7-day free trial!");
            }
            window.location.href = response.data.url;
            return;
          }
        } catch (checkoutErr) {
          const msg = checkoutErr.message === "Network Error"
            ? "Could not connect to payment service — please try again or contact support@adwup.ai"
            : "Could not start checkout — redirecting to plans...";
          toast.error(msg);
          navigate('/pricing', { replace: true });
        }
      }
    } catch (error) {
      void 0;
      let errorMessage;
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message === "Network Error") {
        errorMessage = "We couldn't reach our servers. Please check your connection and try again, or contact support@adwup.ai";
      } else {
        errorMessage = "We could not complete your registration — please try again or contact support@adwup.ai";
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    track(EVENTS.FORGOT_PASSWORD_CLICKED, {}, { email: forgotEmail });
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotEmail });
      toast.success("Password reset link sent! Check your email.");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error) {
      // Show success even if email not found (security best practice)
      toast.success("If an account exists, a reset link has been sent.");
      setShowForgotPassword(false);
      setForgotEmail("");
    } finally {
      setForgotLoading(false);
    }
  };

  const features = [
    { icon: <Cpu className="w-5 h-5" />, text: "AI writes your ad copy & creatives" },
    { icon: <Phone className="w-5 h-5" />, text: "Voice AI calls leads in <60s" },
    { icon: <Shield className="w-5 h-5" />, text: "MLS Guard auto-pauses when sold" },
    { icon: <TrendingUp className="w-5 h-5" />, text: "Campaigns live on Meta in minutes" }
  ];

  return (
    <main id="main-content" className="min-h-screen bg-[#FAFAFA] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-black">
        {/* Grid background */}
        <div className="absolute inset-0 grid-bg-dark" />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Logo - use light version on dark background */}
          <img 
            src="/images/logo-light.png" 
            alt="AdWup" 
            className="h-14 w-auto object-contain"
          />

          <div>
            <h2 className="font-heading text-5xl font-bold mb-6 leading-tight">
              <span className="text-white">JOIN THE</span><br />
              <span className="gradient-text-cyber">ELITE</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-md leading-relaxed">
              AI-powered real estate marketing that generates ads, launches campaigns, and manages follow-up — all in one platform.
            </p>

            <div className="space-y-4">
              {features.map((feature, i) => (
                <motion.div 
                  key={feature.text}
                  {...fadeInLeft}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    {feature.icon}
                  </div>
                  <span className="text-gray-300">{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-gray-400">
            <Shield className="w-5 h-5 text-primary" />
            <span className="text-sm">256-bit encryption • SOC 2 ready • GDPR ready</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <motion.div 
          {...fadeInUp}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src="/images/logo-light.png" 
              alt="AdWup" 
              className="h-12 w-auto"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 relative">
            {/* Mascot greeting on signup mode */}
            {!isLogin && (
              <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-10">
                <Mascot variant="purple-kawaii" size={88} animation="wave" className="drop-shadow-xl" alt="Welcome to AdWup" />
              </div>
            )}
            <h1 className="font-heading text-2xl font-bold mb-2 text-gray-900 uppercase">
              {isLogin ? "Access Portal" : "Create Account"}
            </h1>
            <p className="text-gray-700 mb-6">
              {isLogin 
                ? "Enter credentials to access your command center" 
                : "Start dominating your market in minutes"
              }
            </p>

            {!isLogin && (
              <div
                className={`mb-6 rounded-xl border p-4 bg-gradient-to-r ${planDisplay.accent} bg-opacity-10`}
                data-testid="selected-plan-banner"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/30 backdrop-blur flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/90 font-medium">Signing up for</p>
                      <p className="text-lg font-bold text-white" data-testid="selected-plan-name">
                        {planDisplay.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white tabular-nums">${planDisplay.price}</p>
                    <p className="text-xs text-white/90">
                      {planDisplay.hasTrial ? "after 7-day free trial" : "/ month"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  className="mt-3 text-xs text-white/90 hover:text-white underline-offset-2 hover:underline"
                  data-testid="change-plan-link"
                >
                  Wrong plan? Choose a different one →
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 font-heading uppercase tracking-wide text-xs">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-dark h-12 pl-12"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-heading uppercase tracking-wide text-xs">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@brokerage.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-dark h-12 pl-12"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-heading uppercase tracking-wide text-xs">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark h-12 pl-12 pr-12"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-gray-500 hover:text-primary transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isLoading}
                className="btn-power w-full h-14 text-base font-bold flex items-center justify-center gap-2"
                data-testid="auth-submit-btn"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? "ACCESS COMMAND CENTER" : "ACTIVATE AI ENGINE"} <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-gray-700">
              {isLogin ? "New here? " : "Already have access? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:text-primary/80 font-bold uppercase"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Create Account" : "Sign In"}
              </button>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0.99, scale: 0.95 }}
            animate={fadeInScale.animate}
            className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-2">Reset Password</h2>
            <p className="text-gray-600 mb-6">Enter your email and we'll send you a reset link.</p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="email"
                  placeholder="you@brokerage.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="h-12 pl-12"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 bg-[#39FF14] text-black hover:bg-[#32E012]"
                  data-testid="send-reset-link-btn"
                >
                  {forgotLoading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </main>
  );
}
